import { useCallback, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LockResetIcon from '@mui/icons-material/LockReset';

import { PermissionEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { GeneratedPasswordDialog } from '../../components/GeneratedPasswordDialog';
import { SpeedDial } from '../../components/SpeedDial';
import { useAuthenticationContext } from '../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { usePermissions } from '../../hooks/usePermissions.hook';
import { describeErrors } from '../../utils/back-errors.util';
import { AlertMessages } from '../../components/AlertMessages';

import { useRegeneratePasswordMutation } from './hooks/useRegeneratePasswordMutation.hook';

interface Props {
  readonly userId: Id;
}

// A user manager (UsersManage) regenerates the lost password of another user: shown once,
// to be sent to the user outside e-Socna.
export const RegeneratePasswordAction = ({ userId }: Readonly<Props>) => {
  const { authenticationData } = useAuthenticationContext();
  const { can } = usePermissions();
  const { mutate, error, isPending, reset } = useRegeneratePasswordMutation();

  const [isConfirming, setIsConfirming] = useState(false);
  const [password, setPassword] = useState<string | null>(null);

  const confirm = useCallback(() => {
    reset();
    setIsConfirming(true);
  }, [reset]);

  const cancel = useCallback(() => {
    setIsConfirming(false);
  }, []);

  const regenerate = useCallback(() => {
    mutate(
      { userId },
      {
        onSuccess: ({ regenerateUserPassword }) => {
          setIsConfirming(false);
          setPassword(regenerateUserPassword);
        },
      },
    );
  }, [mutate, userId]);

  const closePassword = useCallback(() => {
    setPassword(null);
  }, []);

  if (!can(PermissionEnum.UsersManage) || authenticationData?._id === userId) {
    return null;
  }

  return (
    <>
      <SpeedDial
        actions={[
          {
            id: 'regeneratePassword',
            icon: <LockResetIcon />,
            name: 'Régénérer le mot de passe',
            onClick: confirm,
          },
        ]}
        title="Actions possibles sur le Compte de l'utilisateur"
      />
      <Dialog fullWidth maxWidth="sm" onClose={cancel} open={isConfirming}>
        <DialogTitle>Régénérer le mot de passe</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Un nouveau mot de passe va être généré et la session de
            l&apos;utilisateur sera fermée. L&apos;ancien mot de passe ne
            fonctionnera plus.
          </DialogContentText>
          {error ? (
            <AlertMessages
              messages={describeErrors(error)}
              severity="error"
              sx={{ mt: 2 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancel} size="large">
            Annuler
          </Button>
          <Button
            disabled={isPending}
            onClick={regenerate}
            size="large"
            variant="contained"
          >
            Régénérer
          </Button>
        </DialogActions>
      </Dialog>
      <GeneratedPasswordDialog
        onClose={closePassword}
        password={password}
        title="Nouveau mot de passe"
      />
    </>
  );
};
